/**
 * This file is a part of the Promvc framework.
 * 
 * (c) ThemePlexi
 * 
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

/**
 * Language methods methods
 * 
 * @version 1.0.0
 */
const LangMethods = ( function () {

	/**
	 * Declare private properties
	 * @private
	 */

	const MAX_SAFE_INTEGER = 9007199254740991;
	const MAX_INTEGER = 1.7976931348623157e+308;

	const MAX_ARRAY_LENGTH = 4294967295;
	const MAX_ARRAY_INDEX = MAX_ARRAY_LENGTH - 1;

	/** Object#toString result reference */
	const tags = {
		argsTag         : '[object Arguments]',
		arrayTag        : '[object Array]',
		asyncTag        : '[object AsyncFunction]',
		boolTag         : '[object Boolean]',
		dateTag         : '[object Date]',
		domExcTag       : '[object DOMException]',
		errorTag        : '[object Error]',
		funcTag         : '[object Function]',
		genTag          : '[object GeneratorFunction]',
		mapTag          : '[object Map]',
		numberTag       : '[object Number]',
		nullTag         : '[object Null]',
		objectTag       : '[object Object]',
		promiseTag      : '[object Promise]',
		proxyTag        : '[object Proxy]',
		regexpTag       : '[object RegExp]',
		setTag          : '[object Set]',
		stringTag       : '[object String]',
		symbolTag       : '[object Symbol]',
		undefinedTag    : '[object Undefined]',
		weakMapTag      : '[object WeakMap]',
		weakSetTag      : '[object WeakSet]',

		arrayBufferTag  : '[object ArrayBuffer]',
		dataViewTag     : '[object DataView]',
		float32Tag      : '[object Float32Array]',
		float64Tag      : '[object Float64Array]',
		int8Tag         : '[object Int8Array]',
		int16Tag        : '[object Int16Array]',
		int32Tag        : '[object Int32Array]',
		uint8Tag        : '[object Uint8Array]',
		uint8ClampedTag : '[object Uint8ClampedArray]',
		uint16Tag       : '[object Uint16Array]',
		uint32Tag       : '[object Uint32Array]'
	};

	/** Used to identify `toStringTag` values of typed arrays. */
	const typedArrayTags = {
		[tags.float32Tag]      : true,
		[tags.float64Tag]      : true,
		[tags.int8Tag]         : true,
		[tags.int16Tag]        : true,
		[tags.int32Tag]        : true,
		[tags.uint8Tag]        : true,
		[tags.uint8ClampedTag] : true,
		[tags.uint16Tag]       : true,
		[tags.uint32Tag]       : true,

		[tags.argsTag]         : false,
		[tags.arrayTag]        : false,
		[tags.arrayBufferTag]  : false,
		[tags.boolTag]         : false,
		[tags.dataViewTag]     : false,
		[tags.dateTag]         : false,
		[tags.errorTag]        : false,
		[tags.funcTag]         : false,
		[tags.mapTag]          : false,
		[tags.numberTag]       : false,
		[tags.objectTag]       : false,
		[tags.regexpTag]       : false,
		[tags.setTag]          : false,
		[tags.stringTag]       : false,
		[tags.weakMapTag]      : false
	};

	/**
	 * Creates a unary function that invokes `func` with its argument transformed.
	 * 
	 * @private
	 * @param   {Function} func      The function to wrap.
	 * @param   {Function} transform The argument transform.
	 * @returns {Function} The new function.
	 */
	function overArg( func, transform )
	{
		return function ( arg ) {
			return func( transform( arg ) );
		};
	}

	/**
	 * Public language methods api.
	 * 
	 * @since 1.0.0
	 */
	const Util = {

		baseGetTag: function ( value )
		{
			if ( value == null ) {
				return value === undefined ? tags.undefinedTag : tags.nullTag;
			}

			return Object.prototype.toString.call( value );
		},

		getTag: function ( tag )
		{
			if ( tags[ tag ] !== undefined ) {
				return tags[ tag ];
			}

			return false;
		},

		isArguments: function ( value )
		{
			return this.isObjectLike( value ) && this.baseGetTag( value ) == tags.argsTag;
		},

		isArray: Array.isArray,

		isArrayBuffer: function ( value )
		{
			return this.isObjectLike( value ) && this.baseGetTag( value ) == tags.arrayBufferTag;
		},

		isArrayLike: function ( value )
		{
			return value !== null && this.isLength( value.length ) && ! this.isFunction( value );
		},

		isDate: function ( value )
		{
			this.isObjectLike( value ) && this.baseGetTag( value ) == tags.dateTag;
		},

		isEmpty: function ( value )
		{
			if ( value === null ) {
				return true;
			}

			if (
				this.isArrayLike( value ) &&
				(
					this.isArray( value ) ||
					typeof value === 'string' ||
					typeof value.splice === 'function' ||
					this.isTypedArray( value ) ||
					this.isArguments( value )
				)
			) {
				return ! value.length;
			}

			const tag = this.baseGetTag( value );
			if ( tag == tags.mapTag || tag == tags.setTag ) {
				return ! value.size;
			}

			if ( this.isPrototype( value ) ) {
				return ! this.keys( value ).length;
			}

			for ( let key in value ) {
				if ( Object.prototype.hasOwnProperty.call( value, key ) ) {
					return false;
				}
			}

			return true;
		},

		isFunction: function ( value )
		{
			if ( ! this.isObject( value ) ) {
				return false;
			}

			// The use of `Object#toString` avoids issues with the `typeof` operator
			// in Safari 9 which returns 'object' for typed arrays and other constructors.
			const tag = this.baseGetTag( value );
			return tag == tags.funcTag || tag == tags.genTag || tag == tags.asyncTag || tag == tags.proxyTag;
		},

		isLength: function ( value )
		{
			return typeof value === 'number' && value > -1 && value % 1 === 0 && value <= MAX_SAFE_INTEGER;
		},

		isObject: function ( value )
		{
			const type = typeof value;
			return value !== null && ( type === 'object' || type === 'function' );
		},

		isObjectLike: function ( value )
		{
			return value !== null && typeof value === 'object';
		},

		isPrototype: function ( value )
		{
			let Ctor = value && value.constructor,
				proto = ( typeof Ctor == 'function' && Ctor.prototype ) || Object.prototype;

			return value === proto;
		},

		isString: function ( value )
		{
			return typeof value === 'string' ||
				( ! this.isArray( value ) && this.isObjectLike( value ) && this.baseGetTag( value ) == tags.stringTag );
		},

		isTypedArray: function ( value )
		{
			return this.isObjectLike( value ) &&
				this.isLength( value.length ) && !! typedArrayTags[ this.baseGetTag( value ) ];
		},

		keys: function ( object )
		{
			if ( ! this.isPrototype( object ) ) {
				return this.nativeKeys( object );
			}

			let result = [];
			for ( let key in Object( object ) ) {
				if ( Object.prototype.hasOwnProperty.call( object, key ) && key != 'constructor' ) {
					result.push( key );
				}
			}

			return result;
		},

		nativeKeys: overArg( Object.keys, Object )

	};

	return Util;

}());

/**
 * Module exports
 */
export { LangMethods };